import React, { useMemo } from 'react'
import { Link } from '@tanstack/react-router'
import { useClerk } from '@clerk/clerk-react'
import { useAuthUser } from '@/context/clerk-context'
import {
  Avatar,
  AvatarFallback,
  AvatarImage,
  Button,
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuGroup,
  DropdownMenuItem,
  DropdownMenuLabel,
  DropdownMenuSeparator,
  DropdownMenuShortcut,
  DropdownMenuTrigger,
} from '@neutrino/ui'

const ProfileDropdown = React.memo(function ProfileDropdown() {
  const { fullName, email, avatarUrl, isLoaded: isUserLoaded } = useAuthUser()
  const { signOut } = useClerk()
  const displayName = isUserLoaded && fullName ? fullName : 'User'
  const displayEmail = isUserLoaded && email ? email : ''
  const displayAvatar =
    isUserLoaded && avatarUrl ? avatarUrl : '/avatars/01.png'

  // Memoize avatar fallback calculation
  const avatarFallback = useMemo(() => {
    return displayName
      .split(' ')
      .map((name) => name[0])
      .join('')
  }, [displayName])

  const handleLogout = async () => {
    try {
      await signOut()
      // Redirect handled by ClerkProvider
    } catch (error) {
      console.error('Logout failed:', error)
    }
  }

  return (
    <DropdownMenu modal={false}>
      <DropdownMenuTrigger asChild>
        <Button variant='ghost' className='relative h-8 w-8 rounded-full'>
          <Avatar className='h-8 w-8'>
            <AvatarImage src={displayAvatar} alt={displayName} />
            <AvatarFallback>{avatarFallback}</AvatarFallback>
          </Avatar>
        </Button>
      </DropdownMenuTrigger>
      <DropdownMenuContent className='w-56' align='end' forceMount>
        <DropdownMenuLabel className='font-normal'>
          <div className='flex flex-col space-y-1'>
            <p className='text-sm leading-none font-medium'>{displayName}</p>
            <p className='text-muted-foreground text-xs leading-none'>
              {displayEmail}
            </p>
          </div>
        </DropdownMenuLabel>
        <DropdownMenuSeparator />
        <DropdownMenuGroup>
          <DropdownMenuItem asChild>
            <Link to='/settings'>
              Profile
              <DropdownMenuShortcut>⇧⌘P</DropdownMenuShortcut>
            </Link>
          </DropdownMenuItem>
          <DropdownMenuItem asChild>
            <Link to='/settings'>
              Billing
              <DropdownMenuShortcut>⌘B</DropdownMenuShortcut>
            </Link>
          </DropdownMenuItem>
          <DropdownMenuItem asChild>
            <Link to='/settings'>
              Settings
              <DropdownMenuShortcut>⌘S</DropdownMenuShortcut>
            </Link>
          </DropdownMenuItem>
          <DropdownMenuItem>New Team</DropdownMenuItem>
        </DropdownMenuGroup>
        <DropdownMenuSeparator />
        <DropdownMenuItem
          onSelect={handleLogout}
          className='w-full justify-start p-2 px-2 font-normal'
        >
          <span className='flex items-center'>
            <svg
              className='mr-2 h-4 w-4'
              stroke='currentColor'
              fill='none'
              strokeWidth='2'
              viewBox='0 0 24 24'
            >
              <path
                strokeLinecap='round'
                strokeLinejoin='round'
                d='M17 16l4-4m0 0l-4-4m4 4H7m6 4v1a2 2 0 01-2 2H7a2 2 0 01-2-2V7a2 2 0 012-2h4a2 2 0 012 2v1'
              />
            </svg>
            Log out
          </span>
        </DropdownMenuItem>
      </DropdownMenuContent>
    </DropdownMenu>
  )
})

export { ProfileDropdown }
